import { CreateUserResponse as CreateUserType } from '@otter-ai/recall-client';
import {
  AccountType,
  BasicWorkspaceAccess,
  SignInCheckEmailErrorEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { PATH_URL } from '#constants';
import {
  useAuthNavigation,
  useCheckPaidMediaOnboardingEligibility,
  useConfirmEmailBySlackToken,
  useCreateBasicWorkspace,
  useHandleWorkspaceInvitation,
  useOnboardSignupAnalytics,
  useSetCurrentUserDepartmentAndRole,
} from '#hooks';
import {
  CheckEmailApiResponse,
  useCheckEmailMutation,
  useConfirmEmailBySharingLinkMutation,
} from '#mutation';
import { loginInfoQueryCreator, userProfileQueryCreator } from '#query';
import { AuthWorkspace, NEW_ONBOARDING_FLOW } from '#types';
import {
  getPartialLoginState,
  getSessionItem,
  isSpeechUrl,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  setInLocalStorage,
  setPartialLoginState,
} from '#utils';

export const useHandleEmailSignupSuccess = ({
  skipRedirect = false,
  skipHandleSignupSuccess = false,
}: {
  skipRedirect?: boolean;
  skipHandleSignupSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();

  const { goToSignedInApp } = useAuthNavigation();
  const { setDepartmentAndRole } = useSetCurrentUserDepartmentAndRole();
  const { checkEmailAsync } = useCheckEmailMutation();
  const { isPaidMediaSignupFlow } = useCheckPaidMediaOnboardingEligibility({});
  const { handleWorkspaceInvitation } = useHandleWorkspaceInvitation();
  const { confirmEmailBySlackTokenAsync } = useConfirmEmailBySlackToken({});
  const { mutateAsync: confirmEmailBySharingLinkAsync } =
    useConfirmEmailBySharingLinkMutation({});
  const { logOnboardSignupSuccessEvent } = useOnboardSignupAnalytics();

  const { createBasicWorkspaceAsync } = useCreateBasicWorkspace({
    isNewUser: true,
    basicWorkspaceAccess: BasicWorkspaceAccess.owner,
    onSuccess: () => {
      if (searchParams.get('dept') === 'sales') {
        setDepartmentAndRole({
          department: 'Sales',
          role: 'Sales',
        });
      }
    },
  });

  const checkUserEmail = async (
    email: string,
  ): Promise<CheckEmailApiResponse | null> => {
    try {
      const response = await checkEmailAsync(email);

      if (response.error) {
        // TODO(hao): fix ErrorStatus missing in checkEmail hook
        trackEvent(
          SignInCheckEmailErrorEvent({
            ErrorEmail: email,
            // ErrorStatus: checkEmailResponse.error.status,
            ErrorCode: response.error.code,
            ErrorMessage: response.error.message,
          }),
        );

        return null;
      }

      return response;
    } catch (_) {
      return null;
    }
  };

  /**
   * Handles sharing token flow
   */
  const failedToConfirmEmailBySharingLink = async () => {
    const sharingToken = getPartialLoginState().sharingToken;
    const isCreateAccount = getSessionItem(
      SESSION_STORAGE_KEYS.IS_CREATE_ACCOUNT,
    );

    if (sharingToken && isCreateAccount) {
      try {
        await confirmEmailBySharingLinkAsync(sharingToken);
      } catch (_) {
        return true;
      }
    }

    return false;
  };

  /**
   * Determines the appropriate onboarding flow based on user context
   */
  const getOnboardingFlow = (
    inviteFlow: boolean,
    checkEmailResponse: CheckEmailApiResponse | null,
    experiments: CreateUserType['experiments'],
    hasSlackToken: boolean,
  ): NEW_ONBOARDING_FLOW => {
    const paidMediaSignupFlow = isPaidMediaSignupFlow(
      experiments,
      checkEmailResponse,
    );

    if (inviteFlow) {
      return NEW_ONBOARDING_FLOW.WORKSPACE_MEMBER;
    }

    const isRedirectUrlUniversalSpeech = isSpeechUrl(
      getPartialLoginState().redirectUrl ?? '',
    );

    const sharedSpeechEmailSignUpFlow =
      isRedirectUrlUniversalSpeech && !hasSlackToken;

    if (sharedSpeechEmailSignUpFlow) {
      return NEW_ONBOARDING_FLOW.SHARED_SPEECH_EMAIL_SIGNUP;
    }

    if (hasSlackToken) {
      return NEW_ONBOARDING_FLOW.SLACK_SIGNUP;
    }

    if (paidMediaSignupFlow) {
      return NEW_ONBOARDING_FLOW.PAID_MEDIA;
    }

    return NEW_ONBOARDING_FLOW.GENERAL_EMAIL;
  };

  /**
   * Sets up redirect URL and query parameters based on user context
   */
  const setupRedirectParams = (
    onboardingFlow: NEW_ONBOARDING_FLOW,
    confirmEmailBySharingLinkFailed: boolean,
    username: string,
  ) => {
    const partialLoginState = getPartialLoginState();
    const workspace = partialLoginState.workspace;

    // Base redirect setup
    const baseRedirectParams = {
      redirectUrl: PATH_URL.WELCOME,
      redirectQueryParams: {
        ...partialLoginState.redirectQueryParams,
        new_onboarding: onboardingFlow,
        ...(partialLoginState.redirectUrl && {
          r: partialLoginState.redirectUrl,
        }),
      },
    };

    setPartialLoginState(baseRedirectParams);

    // Handle SSO required workspace
    if (
      workspace?.is_pending_member === true &&
      workspace.sso_enabled === true &&
      workspace.sso_required === true
    ) {
      setPartialLoginState({
        redirectUrl: PATH_URL.SSO_LOGIN,
      });
      return;
    }

    // old business onboarding flow (not used ???)
    // if (couponCode) {
    // }

    // Handle email verification failed
    if (
      confirmEmailBySharingLinkFailed &&
      partialLoginState.isOptimizedShareEmailSignup
    ) {
      // setPartialLoginState({
      //   redirectUrl: PATH_URL.EMAIL_VERIFY,
      // });
      return;
    }

    // Handle Slack signup
    if (partialLoginState.slackSignupToken) {
      setPartialLoginState({
        ...baseRedirectParams,
        redirectQueryParams: {
          ...baseRedirectParams.redirectQueryParams,
          new_onboarding: NEW_ONBOARDING_FLOW.SLACK_SIGNUP,
        },
      });
      return;
    }

    if (
      partialLoginState.isOptimizedShareEmailSignup ||
      username.endsWith('otterqa.com')
    ) {
      setPartialLoginState({
        redirectUrl: PATH_URL.WELCOME,
        redirectQueryParams: {
          ...partialLoginState.redirectQueryParams,
          new_onboarding: NEW_ONBOARDING_FLOW.GENERAL_EMAIL,
        },
      });

      // TODO(hao): reset partial login state???
    }
  };

  const handleEmailSignupSuccess = async (res: CreateUserType) => {
    // Capture session storage values FIRST, before any operations that might clear them
    const slackToken = getSessionItem<string>(SESSION_STORAGE_KEYS.SLACK_TOKEN);

    await queryClient.invalidateQueries(loginInfoQueryCreator());
    await queryClient.invalidateQueries(userProfileQueryCreator());

    if (!skipHandleSignupSuccess) {
      const invitationToken = getSessionItem<string>(
        SESSION_STORAGE_KEYS.INVITATION_TOKEN,
      );

      const workspace = getSessionItem<AuthWorkspace>(
        SESSION_STORAGE_KEYS.WORKSPACE,
      );

      const { sharingToken } = getPartialLoginState();

      const isCreateAccount = getSessionItem(
        SESSION_STORAGE_KEYS.IS_CREATE_ACCOUNT,
      );

      const hasWorkspaceInvitation = Boolean(invitationToken && workspace?.id);

      const inviteFlow = hasWorkspaceInvitation
        ? await handleWorkspaceInvitation({
            userId: res.user.id.toString(),
            isThirdPartySignup: false,
          })
        : false;

      const confirmEmailBySharingLinkFailed =
        !hasWorkspaceInvitation && sharingToken && isCreateAccount
          ? await failedToConfirmEmailBySharingLink()
          : false;

      const user = res.user;
      const checkEmailResponse = await checkUserEmail(user.email);

      // TODO(hao): implement trackAffiliateConversationEvent
      // analytics.trackAffiliateConversationEvent(user.userid);

      logOnboardSignupSuccessEvent(
        {
          authResp: res,
          checkEmailResp: checkEmailResponse,
          accountType: AccountType.email,
          SourceCodeID: 'SetupProfileMainPage.tsx',
        },
        { checkReferral: true },
      );

      if (!inviteFlow && checkEmailResponse?.data?.status === 'OK') {
        try {
          await createBasicWorkspaceAsync();
        } catch (_) {}
      }

      setInLocalStorage(LOCAL_STORAGE_KEYS.OTTER_SIGNUP_TS, Date.now());

      if (slackToken) {
        try {
          await confirmEmailBySlackTokenAsync(slackToken);
        } catch (_) {}
      }

      const hasSlackToken = !!slackToken;

      const onboardingFlow = getOnboardingFlow(
        inviteFlow,
        checkEmailResponse,
        res.experiments,
        hasSlackToken,
      );

      setupRedirectParams(
        onboardingFlow,
        confirmEmailBySharingLinkFailed,
        res.username,
      );
    }

    goToSignedInApp({
      skipRedirect,
      emailVerifyRequired: res.email_verification_required,
    });
  };

  return { handleEmailSignupSuccess };
};
